import React, { useState, useEffect, useRef } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Badge,
    Modal,
    ModalBody,
    Table,
} from 'reactstrap';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import Select from 'react-select';
import Loader from '../../../Layout/Loader';
import { isOwnRequest } from '../../../Utils/authUtils';

export default function ClosingStockApprovals() {
    const [territories, setTerritories] = useState<any[]>([]);
    const [selectedTerr, setSelectedTerr] = useState<string | number>('');
    const [selectedMonth, setSelectedMonth] = useState<string>(moment().subtract(1, 'month').format('MM-YYYY'));
    const [selectedStatus, setSelectedStatus] = useState<string>('Submitted');
    const [loading, setLoading] = useState<boolean>(false);
    const [closingStockList, setClosingStockList] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const [rejectRemark, setRejectRemark] = useState<string>('');
    const [submittingAction, setSubmittingAction] = useState<boolean>(false);

    // Stock details modal state
    const [stockDetailsModalOpen, setStockDetailsModalOpen] = useState<boolean>(false);
    const [stockDetailsLoading, setStockDetailsLoading] = useState<boolean>(false);
    const [stockDetails, setStockDetails] = useState<any[]>([]);
    const [stockOutletName, setStockOutletName] = useState<string>('');

    // Media modal state
    const [mediaModalOpen, setMediaModalOpen] = useState<boolean>(false);
    const [mediaLoading, setMediaLoading] = useState<boolean>(false);
    const [currentMedia, setCurrentMedia] = useState<{ images: any[]; pdfs: any[] }>({ images: [], pdfs: [] });
    const [selectedMediaTab, setSelectedMediaTab] = useState<'images' | 'pdfs'>('images');
    const mediaMetaCache = useRef<Map<string, any>>(new Map());

    const monthOptions = Array.from({ length: 6 }).map((_, i) => {
        const d = moment().subtract(i, 'months');
        return { label: d.format('MMM YYYY'), value: d.format('MM-YYYY') };
    });

    const statusOptions = [
        { label: 'Submitted', value: 'Submitted' },
        { label: 'Approved', value: 'Approved' },
        { label: 'Rejected', value: 'Rejected' },
    ];

    const currentData = closingStockList[currentIndex] || null;
    const isActionable = currentData?.status === 'Pending' || currentData?.status === 'Submitted';

    const fetchTerritories = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const url = `${serverUrl}${API.getTerritoriesArray}`;
            const response = await getData(url, true);
            let list: any[] = [];
            const d = response?.data?.data || response?.data;
            if (Array.isArray(d)) {
                list = d.filter(Boolean).map((t: any) => ({
                    label: t.label || t.name || t.Stownname || String(t.id ?? t.value ?? t.Itownid),
                    id: t.id ?? t.value ?? t.Itownid
                }));
            } else if (d && typeof d === 'object') {
                list = Object.keys(d).map((key) => ({ label: d[key], id: key }));
            }
            setTerritories(list);
            if (list.length > 0) {
                const userTerrId = localStorage.getItem("territoryId") || localStorage.getItem("territory_id");
                if (userTerrId && list.some((t: any) => String(t.id) === String(userTerrId))) {
                    setSelectedTerr(userTerrId);
                } else {
                    const firstId = list[0].id ?? list[0].value ?? list[0].Itownid;
                    setSelectedTerr(firstId);
                }
            }
        } catch (err) {
            console.error("Error fetching territories:", err);
        }
    };

    const fetchClosingStockData = async (territory_id: string | number, month: string, status: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !territory_id) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.GetAllClosingStock}`;
            const response = await getData(url, true, {
                territory_id,
                month,
                request_status: status,
            });

            if (response.status === 200 || response.data?.statusCode === 200) {
                const rawList = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
                const mappedData = rawList
                    .filter((item: any) => item.OutletClosingStockRequestId != null)
                    .map((item: any) => ({
                        requestId: item.OutletClosingStockRequestId,
                        outletName: item.OutletName || 'N/A',
                        outletCode: item.OutletCode || 'N/A',
                        outletContact: item.OutletContactNo || 'N/A',
                        territoryName: item.TerritoryName || 'N/A',
                        beatName: item.BeatName || 'N/A',
                        status: item.RequestStatus || 'Pending',
                        requestDate: item.RequestDate?.date ? moment(item.RequestDate.date).format('DD MMMM YYYY') : 'N/A',
                        month,
                        outletOrgId: item.OutletOrgId,
                        requestMedia: item.RequestMedia,
                        CreatedBy: item.CreatedBy ?? item.created_by,
                        CreatedByPosition: item.CreatedByPosition ?? item.created_by_position,
                        EmployeeId: item.EmployeeId ?? item.employee_id,
                    }));

                setClosingStockList(mappedData);
                setCurrentIndex(0);
            } else {
                setClosingStockList([]);
                setCurrentIndex(0);
            }
        } catch (err) {
            console.error("Error fetching closing stock data:", err);
            setClosingStockList([]);
            setCurrentIndex(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTerritories();
    }, []);

    useEffect(() => {
        if (selectedTerr && selectedMonth && selectedStatus) {
            fetchClosingStockData(selectedTerr, selectedMonth, selectedStatus);
        }
    }, [selectedTerr, selectedMonth, selectedStatus]);

    const submitStatusUpdate = async (type: 'Approved' | 'Rejected') => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !currentData?.requestId) {
            toast.error("Missing required data to submit status update");
            return;
        }

        if (type === 'Rejected' && !rejectRemark) {
            toast.error("Please select a reason to reject the request");
            return;
        }

        setSubmittingAction(true);
        try {
            const url = `${serverUrl}${API.UpdateClosingStockStatus}`;
            const params: any = {
                request_id: currentData.requestId,
                request_status: type,
            };
            if (type === 'Rejected') {
                params.request_remark = rejectRemark;
            }

            const response = await postData(url, true, params);

            if (response.status === 200 || response.data?.statusCode === 200) {
                toast.success(`Closing Stock ${type}d successfully!`);
                setRejectRemark('');
                fetchClosingStockData(selectedTerr, selectedMonth, selectedStatus);
            } else {
                toast.error(response.data?.message || `Failed to ${type.toLowerCase()} closing stock request`);
            }
        } catch (err: any) {
            console.error("Error updating status:", err);
            toast.error(err?.message || "An error occurred while updating status");
        } finally {
            setSubmittingAction(false);
        }
    };

    const handleViewStockDetails = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !currentData?.requestId) return;
        setStockOutletName(currentData.outletName);
        setStockDetailsLoading(true);
        setStockDetailsModalOpen(true);
        try {
            const url = `${serverUrl}${API.GetAllClosingStockDetails}`;
            const response = await getData(url, true, { request_id: currentData.requestId });
            if (response.status === 200 || response.data?.statusCode === 200) {
                const list = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
                setStockDetails(list);
            } else {
                setStockDetails([]);
            }
        } catch (err) {
            console.error("Error fetching stock details:", err);
            setStockDetails([]);
        } finally {
            setStockDetailsLoading(false);
        }
    };

    const resolveMediaByIds = async (rawMediaStr: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !rawMediaStr) return;
        setMediaLoading(true);
        const ids = String(rawMediaStr).split(',').map(s => s.trim()).filter(Boolean);
        const images: any[] = [];
        const pdfs: any[] = [];

        for (const id of ids) {
            try {
                let meta = mediaMetaCache.current.get(id);
                if (!meta) {
                    const url = `${serverUrl}${API.downloadFile}?mediaid=${id}`;
                    const res = await getData(url, true);
                    meta = res?.data || {};
                    mediaMetaCache.current.set(id, meta);
                }
                const isPdf = meta?.mediatype && String(meta.mediatype).toLowerCase().includes('pdf');
                const fileUrl = meta?.url || `${serverUrl}media?id=${id}`;
                if (isPdf) {
                    pdfs.push({ id, pdfurl: meta?.pdfurl || fileUrl, name: meta?.name || `Document-${id}` });
                } else {
                    images.push({ id, url: fileUrl });
                }
            } catch (e) {
                images.push({ id, url: `${serverUrl}media?id=${id}` });
            }
        }

        setCurrentMedia({ images, pdfs });
        setSelectedMediaTab(images.length > 0 ? 'images' : 'pdfs');
        setMediaLoading(false);
        setMediaModalOpen(true);
    };

    const terrOptions = React.useMemo(() => {
        if (!Array.isArray(territories)) return [];
        return territories.filter(Boolean).map((t: any) => {
            const val = t?.id ?? t?.value ?? t?.Itownid ?? '';
            const lbl = t?.label || t?.name || t?.Stownname || String(val || 'Unknown');
            return { value: String(val), label: String(lbl) };
        });
    }, [territories]);

    return (
        <div className="p-3" style={{ color: '#000000' }}>
            {/* Filter Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 p-3 bg-light border rounded">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ minWidth: '260px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Territory</label>
                        <Select
                            options={terrOptions}
                            value={terrOptions.find((t: any) => String(t.value) === String(selectedTerr)) || null}
                            onChange={(opt: any) => setSelectedTerr(opt ? opt.value : '')}
                            isSearchable={true}
                            placeholder="Search Territory..."
                            className="text-dark fw-semibold"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#dee2e6',
                                    minHeight: '38px',
                                    color: '#000000',
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: '#000000',
                                    fontWeight: '600',
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    color: '#000000',
                                    backgroundColor: state.isFocused ? '#e9ecef' : '#ffffff',
                                }),
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '150px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Month</label>
                        <Input
                            type="select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bg-white border text-dark shadow-none fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#000000' }}
                        >
                            {monthOptions.map((m, idx) => (
                                <option key={idx} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </Input>
                    </div>

                    <div style={{ minWidth: '150px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Status</label>
                        <Input
                            type="select"
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="bg-white border text-dark shadow-none fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#000000' }}
                        >
                            {statusOptions.map((s, idx) => (
                                <option key={idx} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </Input>
                    </div>
                </div>

                {/* Pagination */}
                {closingStockList.length > 0 && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            color="dark"
                            outline
                            size="sm"
                            disabled={currentIndex === 0}
                            onClick={() => setCurrentIndex(prev => Math.max(prev - 1, 0))}
                            className="fw-bold"
                        >
                            <FeatherIconCom iconName="ChevronLeft" size={16} />
                        </Button>
                        <span className="fw-bold px-3 py-1 bg-white border rounded text-dark" style={{ fontSize: '0.95rem' }}>
                            {currentIndex + 1} / {closingStockList.length}
                        </span>
                        <Button
                            color="dark"
                            outline
                            size="sm"
                            disabled={currentIndex >= closingStockList.length - 1}
                            onClick={() => setCurrentIndex(prev => Math.min(prev + 1, closingStockList.length - 1))}
                            className="fw-bold"
                        >
                            <FeatherIconCom iconName="ChevronRight" size={16} />
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <Loader height="250px" />
            ) : currentData ? (
                <div>
                    {/* Closing Stock Overview Card */}
                    <Card className="border shadow-sm mb-4">
                        <CardBody className="p-4" style={{ color: '#000000' }}>
                            <h5 className="fw-bold text-dark border-bottom pb-2 mb-3" style={{ color: '#000000' }}>
                                <FeatherIconCom iconName="Box" size={18} className="me-2 text-primary" />
                                Closing Stock Overview
                            </h5>

                            <div className="p-3 bg-light border rounded mb-3">
                                {[
                                    { label: 'Outlet Name', value: currentData.outletName },
                                    { label: 'Outlet Code', value: currentData.outletCode },
                                    { label: 'Contact', value: currentData.outletContact },
                                    { label: 'Territory', value: currentData.territoryName },
                                    { label: 'Beat', value: currentData.beatName },
                                    { label: 'Status', value: currentData.status },
                                    { label: 'Request Date', value: currentData.requestDate },
                                ].map((row, idx) => (
                                    <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                        <span className="fw-bold text-dark">{row.label}:</span>
                                        <span className="fw-bold text-primary">{row.value}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Attachments Section */}
                            {currentData.requestMedia && (
                                <div className="mb-3">
                                    <Button
                                        color="primary"
                                        outline
                                        className="py-2 px-3 fw-bold d-flex align-items-center gap-2"
                                        onClick={() => resolveMediaByIds(currentData.requestMedia)}
                                    >
                                        <FeatherIconCom iconName="Paperclip" size={16} />
                                        <span>View Attached Files ({currentData.requestMedia.split(',').filter(Boolean).length})</span>
                                    </Button>
                                </div>
                            )}

                            {/* View Stock Details Button */}
                            <Button
                                color="success"
                                className="w-100 py-2 fw-bold text-white d-flex align-items-center justify-content-center gap-2 border-0"
                                style={{ backgroundColor: '#198754', fontSize: '1rem' }}
                                onClick={handleViewStockDetails}
                            >
                                <FeatherIconCom iconName="Eye" size={18} />
                                <span>View Stock Details</span>
                            </Button>
                        </CardBody>
                    </Card>

                    {/* Actions Card */}
                    {isActionable ? (
                        !isOwnRequest(currentData) ? (
                        <Card className="border shadow-sm mb-4">
                            <CardBody className="p-4" style={{ color: '#000000' }}>
                                <Row className="g-3 align-items-end">
                                    <Col md={4}>
                                        <Button
                                            color="success"
                                            className="w-100 py-2 fw-bold text-white border-0"
                                            disabled={submittingAction}
                                            style={{ backgroundColor: '#198754', fontSize: '1rem' }}
                                            onClick={() => submitStatusUpdate('Approved')}
                                        >
                                            {submittingAction ? <Spinner size="sm" color="light" /> : "Approve Closing Stock"}
                                        </Button>
                                    </Col>

                                    <Col md={5}>
                                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Rejection Remark</label>
                                        <Input
                                            type="select"
                                            value={rejectRemark}
                                            onChange={(e) => setRejectRemark(e.target.value)}
                                            className="bg-white border text-dark shadow-none fw-semibold py-2"
                                            style={{ fontSize: '0.9rem', color: '#000000' }}
                                        >
                                            <option value="">Select Reason for Rejection</option>
                                            <option value="Incorrect Data">Incorrect Data</option>
                                            <option value="Missing Information">Missing Information</option>
                                            <option value="Stock Mismatch">Stock Mismatch</option>
                                            <option value="Other">Other</option>
                                        </Input>
                                    </Col>

                                    <Col md={3}>
                                        <Button
                                            color="danger"
                                            className="w-100 py-2 fw-bold text-white border-0"
                                            disabled={submittingAction || !rejectRemark}
                                            style={{ backgroundColor: '#dc3545', fontSize: '1rem' }}
                                            onClick={() => submitStatusUpdate('Rejected')}
                                        >
                                            {submittingAction ? <Spinner size="sm" color="light" /> : "Reject Closing Stock"}
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                        ) : (
                            <div className="p-3 bg-light-warning border border-warning rounded text-center fw-bold text-dark mb-4">
                                Approval controls are restricted for your own requests.
                            </div>
                        )
                    ) : (
                        <div className="p-3 bg-light-warning border border-warning rounded text-center fw-bold text-dark mb-4">
                            Approval controls are disabled for this request ({currentData.status}).
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-5 border rounded bg-white">
                    <FeatherIconCom iconName="Inbox" size={48} className="mb-3 text-secondary" />
                    <h5 className="fw-bold text-dark">No Closing Stock Requests Found</h5>
                    <p className="text-muted small">No closing stock requests found for the selected territory, month, and status.</p>
                </div>
            )}

            {/* Stock Details Modal */}
            <Modal isOpen={stockDetailsModalOpen} toggle={() => setStockDetailsModalOpen(!stockDetailsModalOpen)} centered size="lg">
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">Closing Stock Details - {stockOutletName}</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setStockDetailsModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {stockDetailsLoading ? (
                        <Loader height="150px" />
                    ) : stockDetails.length > 0 ? (
                        <Table responsive bordered hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="fw-bold text-dark">Product Name</th>
                                    <th className="fw-bold text-dark">Closing Stock Qty</th>
                                    <th className="fw-bold text-dark">Batch / Exp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockDetails.map((item: any, sIdx: number) => (
                                    <tr key={sIdx}>
                                        <td className="fw-semibold text-dark">{item.ProductName || item.product_name || 'N/A'}</td>
                                        <td className="fw-bold text-dark">{item.Qty || item.qty || 0}</td>
                                        <td className="fw-semibold text-dark">{item.BatchNo || item.ExpiryDate ? `${item.BatchNo || '-'} / ${item.ExpiryDate || '-'}` : 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-4 text-muted small">No closing stock details available</div>
                    )}
                </ModalBody>
            </Modal>

            {/* Media Viewer Modal */}
            <Modal isOpen={mediaModalOpen} toggle={() => setMediaModalOpen(!mediaModalOpen)} centered size="lg">
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">Attached Files</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setMediaModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {mediaLoading ? (
                        <Loader height="150px" />
                    ) : (
                        <div>
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    color={selectedMediaTab === 'images' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('images')}
                                >
                                    Images ({currentMedia.images.length})
                                </Button>
                                <Button
                                    color={selectedMediaTab === 'pdfs' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('pdfs')}
                                >
                                    PDF Documents ({currentMedia.pdfs.length})
                                </Button>
                            </div>

                            {selectedMediaTab === 'images' && (
                                currentMedia.images.length > 0 ? (
                                    <Row className="g-3">
                                        {currentMedia.images.map((img, i) => (
                                            <Col md={6} key={i}>
                                                <div className="border rounded p-2 text-center bg-light">
                                                    <img src={img.url} alt={`Media ${i}`} style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : <div className="text-muted text-center py-3">No images available</div>
                            )}

                            {selectedMediaTab === 'pdfs' && (
                                currentMedia.pdfs.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                        {currentMedia.pdfs.map((pdf, i) => (
                                            <div key={i} className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                                                <span className="fw-bold text-dark">{pdf.name}</span>
                                                <a href={pdf.pdfurl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                                    Open / Download PDF
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="text-muted text-center py-3">No PDF documents available</div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}
